import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserRoleService } from '../services/user-role.service';
import { Role } from '../shared/models/role';
import { User } from '../shared/models/user';
import { PpsUtilityService } from '../services/pps-utility.service';
import { RoleFormComponent } from './role-form.component';
import { ToastService } from '../shared/toast/toast.service';
import { UserSelectionFormComponent } from './user-selection-form.component';
import { forkJoin } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SessionService } from '../services/session.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { SubSink } from 'subsink';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'pps-user-role-management',
  templateUrl: './user-role-management.component.html',
  styleUrls: ['./user-role-management.component.css']
})
export class UserRoleManagementComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  smallScreen = false;

  spinnerName: string = "spinner";
  spinnerText: string = "Loading data ...";
  
  items: MenuItem[] = [
    {label: "Admin", icon: "pi-fw fas fa-user-lock"},
    {label: "Role Users", icon: "pi-fw fas fa-users"},
  ];

  roleLoading: boolean = false;
  userLoading: boolean = false;

  roles: Role[] = [];
  roleGridHeaders: any = [];
  userList: User[] = [];

  roleUsers: User[] = [];
  roleUserGridHeaders: any = [];

  context:any = {};
  selectedRole: Role = null;
  selectedRoleUser: User = null;

  private roleGridApi: any;
  private userGridApi: any;

  constructor(private _userRoleService: UserRoleService,
              private _util: PpsUtilityService,
              private _toastService: ToastService,
              private _dialog: DialogService,
              private _confirmService: ConfirmationService,
              private _sessionService: SessionService,
              private _spinner: NgxSpinnerService,
              private breakpointObserver: BreakpointObserver) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.roleGridHeaders = this.getRoleGridHeader();
    this.loadRoles();

    this.roleUserGridHeaders = this.getRoleUserGridHeader();
  }

  onRoleGridReady(params) {
    this.roleGridApi = params.api;
  }

  onUserGridReady(params) {
    this.userGridApi = params.api;
  }

  onRoleSelectionChanged(params){
    this.selectedRoleUser = null;
    var selectedRows = this.roleGridApi.getSelectedRows();
    this.selectedRole = selectedRows.length === 1 ? selectedRows[0] : null;
    this.loadRoleUsers(this.selectedRole.id);
  }

  onRoleUserSelectionChanged(params){
    var selectedRows = this.userGridApi.getSelectedRows();
    this.selectedRoleUser = selectedRows.length === 1 ? selectedRows[0] : null;
  }

  onRoleRowDblClicked(params){
    this.openRoleForm(params.data);
  }

  loadRoles(){
    this.roleLoading = true;
    this.roles = [];
    this.userList = [];
    this.selectedRole = null;
    this.selectedRoleUser = null;
    
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._userRoleService.getRoles()
      .subscribe(res=>{
        if (res.succeed){
          this.roles = res.result;
          this.loadUsers();
        }else{
          this._toastService.failed("Get roles failed: " + res.errors.join(";"));
        }
      }, 
      _ => {}, 
      () => this.roleLoading = false)
      .add(() => this._spinner.hide(this.spinnerName));
  }

  loadUsers(){
    if (this.userList.length === 0){
      this.userList = [];
      this.selectedRoleUser = null;
      this.subs.sink = this._userRoleService.getUsers()
        .subscribe(res=>{
          if (res.succeed){
            this.userList = res.result;
          }else{
            this._toastService.failed("Get users failed: " + res.errors.join(";"));
          }
        }, _ => {}, () => this.roleLoading = false)
    }
  }

  loadRoleUsers(roleId: number){
    this.userLoading = true;
    this.roleUsers = [];
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._userRoleService.getUsersByRole(roleId)
      .subscribe(res=>{
        if (res.succeed){
          this.roleUsers = res.result;
        }
      }, _ => {}, () => this.userLoading = false)
      .add(() => this._spinner.hide(this.spinnerName));
  }

  openRoleForm(role){
    if (role === null){
      role = new Role();
      role.id = 0;
      role.roleName = "";
      role.roleDescription = "";
    }

    let dialog = this._dialog.open(RoleFormComponent, {
      header: role.id === 0 ? "Create New Role" : "Edit Role",
      styleClass: "size-sm",
      data: {
        role: role
      }
    });

    this.subs.sink = dialog.onClose.subscribe(role => {
      if (role){
        if (role.id === 0){
          this.saveNewRole(role);
        }else{
          this.updateRole(role);
        }
      }
    }, _ => {});
  }

  saveNewRole(role: Role){
    this.roleLoading = true;
    this.subs.sink = this._userRoleService
      .addRole(role)
      .subscribe(res => {
        if (res.succeed){
          this.loadRoles();
          this._toastService.succeeded("Role created!");
        }else{
          this._toastService.failed("Failed to create role: " + res.errors.join(";"));
        }
      }, _ => {}, () => this.roleLoading = false);
  }

  updateRole(role: Role){
    this.roleLoading = true;
    this.subs.sink = this._userRoleService
      .updateRole(role)
      .subscribe(res => {
        if (res.succeed){
          this.loadRoles();
          this._toastService.succeeded("Role update succeeded!");
        }else{
          this._toastService.failed("Role update failed! " + res.errors.join(";"));
        }
      }, _ => {}, () => this.roleLoading = false);
  }

  assignUser(){
    if (this.selectedRole === null){
      this._toastService.failed("Please select a role first");
      return;
    }

    let dialog = this._dialog.open(UserSelectionFormComponent, {
      header: "Assign Users To Role",
      styleClass: "size-sm",
      data: {
        userList: this.userList
      }
    });

    this.subs.sink = dialog.onClose.subscribe(users => {
      if (users){
        if (users.length > 0){
          this.addUsersToRole(this.selectedRole, users);
        }
      }},
      _ => {}
    );
  }

  addUsersToRole(role: Role, users: User[]){
    let tasks = [];
    users.forEach(user => {
      tasks.push(this._userRoleService.addUserToRole(role.id, user));
    });

    this.subs.sink = forkJoin(tasks)
      .subscribe(results => {
        var goodCount = 0;

        results.forEach(r => {
          goodCount += r["succeed"] ? 1 : 0
        })

        this.loadRoleUsers(role.id);
        this.loadRoles();
        this._sessionService.refreshUserContext();
        let msg = `${goodCount} user assigned to role ${role.roleName} !`;
        this._toastService.succeeded(msg);
      });
  }

  removeUser(){
    if (this.selectedRole === null){
      this._toastService.failed("Please select a role first");
      return;
    }

    if (this.selectedRoleUser === null){
      this._toastService.failed("Please select a user to remove");
      return;
    }

    this._confirmService.confirm({
        header: "Remove User From Role",
        message: `
          Are you sure that you want to remove user:
          [${this._util.getEmployeeName(this.selectedRoleUser)}] from role:
          [${this.selectedRole.roleName}]?
        `,
        icon: "fas fa-exclamation-circle icon-danger",
        defaultFocus: "accept",
        accept: () => {
          this.subs.sink = this._userRoleService.deleteUserFromRole(this.selectedRole.id, this.selectedRoleUser.id)
            .subscribe(res => {
              if (res.succeed){
                this.loadRoleUsers(this.selectedRole.id);
                this._sessionService.refreshUserContext();
                this._toastService.succeeded(`
                  User [${this._util.getEmployeeName(this.selectedRoleUser)}]
                  removed from role [${this.selectedRole.roleName}]`);
              }else{
                this._toastService.failed("Remove user failed: " + res.errors.join(";"));
              }
            })
        }});
  }

  private getRoleGridHeader(){
    var cols = [
      {
        headerName: 'Role',
        field:'roleName',
        filter: true,
        sortable: true,
        width: 180
      },
      {
        headerName: 'Total Users',
        field:'userCount',
        filter: false,
        sortable: false,
        width: 80,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => this._util.formatNumber(params.data[params.colDef.field], 0)
      },
      {
        headerName: 'Role Description',
        field:'roleDescription',
        filter: true,
        sortable: true,
        flex: 1,
        suppressSizeToFit: false
      }
    ];

    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }

    return cols;
  }

  private getRoleUserGridHeader(){
    var cols = [
      {
        headerName: 'Username',
        field:'zoesenUsername',
        filter: true,
        sortable: true,
        width: 120
      },
      {
        headerName: 'User Full Name',
        field:'fullName',
        filter: true,
        sortable: true,
        width: 200,
        cellRenderer: this.getEmployeeName
      },
      {
        headerName: 'Email',
        field:'email',
        filter: true,
        sortable: true,
        width: 250
      },
      {
        headerName: 'UserId',
        field:'zoesenUserId',
        filter: false,
        sortable: false,
        flex: 1,
        width: 380
      }
    ];

    return cols;
  }

  private getEmployeeName(params){
    let user: User = params.data;
    let employeeName = params.context.componentParent._util.getEmployeeName({firstName: user.firstName, lastName: user.lastName});
    return employeeName;
  }
}
